import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, BookOpen } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/catalogue", label: "Product Catalogue", icon: BookOpen },
  { to: "/solutions", label: "Hospitality Solutions" },
  { to: "/customers", label: "Our Customers" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
        scrolled || open
          ? "bg-ivory/90 backdrop-blur-xl border-navy/10 shadow-[0_10px_30px_-15px_rgba(10,17,40,0.15)]"
          : "bg-ivory/60 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-28">
        <Link to="/" data-testid="brand-logo-link" className="group flex items-center">
          <img
            src="/assets/logo.png"
            alt="Amazing Grace — Hospitality Textiles"
            className="h-20 md:h-24 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-[13px] tracking-[0.08em] uppercase transition-colors duration-300 hover:text-gold ${
                  isActive ? "text-gold" : "text-navy/80"
                }`
              }
            >
              {l.icon && <l.icon size={14} strokeWidth={1.8} />}
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/quote"
            data-testid="get-quote-button"
            className="hidden sm:inline-flex group items-center gap-2 bg-navy text-ivory text-[13px] tracking-[0.1em] uppercase px-6 py-3 transition-colors duration-300 hover:bg-gold hover:text-navy"
          >
            Get a Quote
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-navy p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-navy/10 px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2.5 font-serif text-2xl ${isActive ? "text-gold italic" : "text-navy"}`
              }
            >
              {l.icon && <l.icon size={20} strokeWidth={1.6} className="text-gold" />}
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/quote"
            data-testid="mobile-get-quote-button"
            className="mt-2 inline-flex items-center justify-center gap-2 bg-navy text-ivory text-sm tracking-[0.1em] uppercase px-6 py-4"
          >
            Get a Quote <ArrowRight size={15} />
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
